/**
 * /settings/guardrails — the document list (or the first-run card), plus the
 * upload launcher. Every other view is its own route (see GuardrailsRoutes):
 * picking a file extracts + formats it here, then navigates to /review with
 * the draft in navigation state. Only the transient "formatting…" wait is
 * local state — it's the format call's loading UI, not a place.
 *
 * The aggregate active-character budget is deliberately not shown. It reaches
 * the admin only as the API's 400 message when a save would overflow it.
 */
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Banner, Button, Skeleton } from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import { useMe } from '../../hooks/useMe.js';
import { useUsersListLogic } from '../users/useUsersListLogic.js';
import { usePoliciesListLogic, useFormatPolicyLogic } from './useGuardrailsLogic.js';
import { GuardrailsFirstRun } from './GuardrailsFirstRun.js';
import { PolicyCard } from './PolicyCard.js';
import {
  extractUpload,
  formatRequestBodyFor,
  sourceTextFor,
  FILE_ACCEPT_ATTR,
} from './fileExtract.js';
import { GUARDRAILS_PATH, downloadTextFile, type ReviewLocationState } from './shared.js';
import { GUARDRAIL_TEMPLATE, TEMPLATE_FILENAME } from './template.js';

const SUBTITLE =
  'Policy documents the assistant treats as hard constraints — rules of the form “in this ' +
  'situation, never instruct this; do this instead.” Active documents are enforced on every ' +
  'conversation turn.';

export const GuardrailsPage = () => {
  const navigate = useNavigate();
  const meQuery = useMe();
  const tenantId = meQuery.data?.tenant.id;
  const policiesQuery = usePoliciesListLogic(tenantId);
  const usersQuery = useUsersListLogic(tenantId);
  const formatLogic = useFormatPolicyLogic();

  const [formattingFile, setFormattingFile] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const openFilePicker = () => {
    setUploadError(null);
    fileInputRef.current?.click();
  };

  const handleFilePicked = async (file: File) => {
    setUploadError(null);
    setFormattingFile(file.name);
    try {
      const extracted = await extractUpload(file);
      const result = await formatLogic.format(formatRequestBodyFor(file.name, extracted));
      const state: ReviewLocationState = {
        draft: {
          filename: file.name,
          size: file.size,
          document: result.document,
          rulesCount: result.rulesCount,
          leftOut: result.leftOut,
          sourceText: sourceTextFor(extracted),
        },
      };
      navigate(`${GUARDRAILS_PATH}/review`, { state });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Could not read that file.');
      setFormattingFile(null);
    }
  };

  if (meQuery.isLoading || !tenantId || policiesQuery.isLoading) {
    return (
      <AppShell>
        <div className="mb-6">
          <Skeleton className="mb-2 h-7 w-56" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="space-y-3.5">
          {Array.from({ length: 2 }, (_, i) => (
            <Skeleton key={i} className="h-28 w-full rounded-xl" />
          ))}
        </div>
      </AppShell>
    );
  }

  const policies = policiesQuery.data ?? [];
  const users = usersQuery.data?.users ?? [];

  // One picker for both entry points — the header button and the first-run card.
  const filePicker = (
    <input
      ref={fileInputRef}
      type="file"
      accept={FILE_ACCEPT_ATTR}
      className="hidden"
      aria-label="Policy document to upload"
      onChange={(e) => {
        const file = e.target.files?.[0];
        // Clear the input so re-picking the same file fires change again.
        e.target.value = '';
        if (file) void handleFilePicked(file);
      }}
    />
  );

  if (formattingFile) {
    return (
      <AppShell>
        <div>
          <h2 className="text-ink mb-1.5 text-2xl font-bold">Formatting your document…</h2>
          <p className="text-ink-muted mb-5 text-sm">
            Reading {formattingFile} and turning it into a constraint catalog. This takes up to a
            minute or two — nothing is saved or enforced yet.
          </p>
          <div className="border-outline bg-surface-elevated space-y-3 rounded-xl border p-5">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className={`h-4 ${i % 3 === 0 ? 'w-2/3' : 'w-full'}`} />
            ))}
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      {filePicker}
      <div>
        <div className="mb-1.5 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-ink text-2xl font-bold">Safety &amp; Guardrails</h2>
          {policies.length > 0 && (
            <div className="flex flex-none gap-2">
              <Button onClick={() => navigate(`${GUARDRAILS_PATH}/add`)}>New rule</Button>
              <Button variant="secondary" onClick={() => openFilePicker()}>
                Upload document
              </Button>
            </div>
          )}
        </div>
        <p className="text-ink-muted mb-5 max-w-[72ch] text-sm">{SUBTITLE}</p>

        {policiesQuery.error && (
          <Banner variant="danger" className="mb-4">
            <Banner.Body>
              <Banner.Title>{policiesQuery.error.message}</Banner.Title>
            </Banner.Body>
          </Banner>
        )}

        {uploadError && (
          <Banner variant="danger" className="mb-4">
            <Banner.Body>
              <Banner.Title>{uploadError}</Banner.Title>
            </Banner.Body>
          </Banner>
        )}

        {policies.length === 0 ? (
          <GuardrailsFirstRun
            onAddRule={() => navigate(`${GUARDRAILS_PATH}/add`)}
            onUpload={() => openFilePicker()}
            onDownloadTemplate={() => downloadTextFile(TEMPLATE_FILENAME, GUARDRAIL_TEMPLATE)}
          />
        ) : (
          <div className="space-y-3.5">
            {policies.map((policy) => (
              <PolicyCard
                key={policy.id}
                tenantId={tenantId}
                policy={policy}
                users={users}
                onOpen={(p) => navigate(`${GUARDRAILS_PATH}/${encodeURIComponent(p.id)}`)}
              />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
};
